import type { CheckDefinition } from "./types";

/**
 * Technical catalog aligned with public agent-readiness checks.
 * Weight 0 checks are recorded and shown. They do not change the score.
 * Version: agent-readiness-2026-10
 */
export const AGENT_READINESS_CHECKS: CheckDefinition[] = [
  {
    id: "robots_txt",
    title: "robots.txt",
    category: "Discoverability",
    specUrl: "https://developers.google.com/search/docs/crawling-indexing/robots/intro",
    maturity: "established",
    detection: "http",
    weight: 1,
    impact: "Crawlers need a robots file to know which pages they may read.",
    fixId: "robots_sitemap",
  },
  {
    id: "xml_sitemap",
    title: "XML sitemap",
    category: "Discoverability",
    specUrl: "https://www.sitemaps.org/protocol.html",
    maturity: "established",
    detection: "http",
    weight: 1,
    impact: "A sitemap gives agents a list of pages without guessing URLs.",
    fixId: "robots_sitemap",
  },
  {
    id: "link_headers",
    title: "HTTP Link headers",
    category: "Discoverability",
    specUrl: "https://www.rfc-editor.org/rfc/rfc8288",
    maturity: "established",
    detection: "http",
    weight: 1,
    impact: "Link headers point agents at catalogs, cards, and alternate formats.",
    fixId: "link_headers",
  },
  {
    id: "dns_aid",
    title: "DNS-AID",
    category: "Discoverability",
    specUrl: "https://datatracker.ietf.org/doc/draft-mozleywilliams-dnsop-dnsaid/",
    maturity: "emerging",
    detection: "dns",
    weight: 1,
    impact: "A DNS agent index lets a resolver find agent services for the domain.",
    fixId: "mcp",
  },
  {
    id: "markdown_negotiation",
    title: "Markdown negotiation",
    category: "Content",
    specUrl: "https://blog.cloudflare.com/agent-readiness",
    maturity: "established",
    detection: "http",
    weight: 1,
    impact: "Agents that ask for text/markdown get a cleaner copy of the page.",
    fixId: "markdown",
  },
  {
    id: "semantic_html",
    title: "Semantic HTML",
    category: "Content",
    specUrl: "https://html.spec.whatwg.org/multipage/sections.html",
    maturity: "established",
    detection: "html",
    weight: 1,
    impact: "A main region and a heading tell an agent where the page content starts.",
    fixId: "website_optimization",
  },
  {
    id: "machine_readable_main",
    title: "Machine-readable main content",
    category: "Content",
    specUrl: "https://html.spec.whatwg.org/multipage/semantics.html",
    maturity: "established",
    detection: "html",
    weight: 1,
    impact: "Structured or clearly marked content is easier to quote without scraping noise.",
    fixId: "markdown",
  },
  {
    id: "llms_txt",
    title: "llms.txt",
    category: "Content",
    specUrl: "https://llmstxt.org/",
    maturity: "optional",
    detection: "http",
    weight: 0,
    impact: "An llms.txt file is an optional guide. Missing it does not change the score.",
    fixId: "markdown",
  },
  {
    id: "ai_crawler_rules",
    title: "AI bot rules in robots.txt",
    category: "Bot access",
    specUrl: "https://developers.google.com/search/docs/crawling-indexing/robots/intro",
    maturity: "established",
    detection: "http",
    weight: 1,
    impact: "Published crawler tokens such as ClaudeBot and GPTBot follow robots.txt.",
    fixId: "robots_sitemap",
  },
  {
    id: "content_signals",
    title: "Content Signals in robots.txt",
    category: "Bot access",
    specUrl: "https://contentsignals.org/",
    maturity: "emerging",
    detection: "http",
    weight: 1,
    impact: "A Content-Signal line tells crawlers how the page may be used.",
    fixId: "robots_sitemap",
  },
  {
    id: "web_bot_auth",
    title: "Web Bot Auth request signing",
    category: "Bot access",
    specUrl: "https://datatracker.ietf.org/doc/draft-meunier-web-bot-auth-architecture/",
    maturity: "optional",
    detection: "http",
    weight: 0,
    impact: "Request signing is for a site that sends signed requests. A content site is not scored on it.",
    fixId: "integration",
  },
  {
    id: "api_catalog",
    title: "API catalog",
    category: "Protocol discovery",
    specUrl: "https://www.rfc-editor.org/rfc/rfc9727",
    maturity: "emerging",
    detection: "http",
    weight: 1,
    impact: "An API catalog lists machine endpoints in one place.",
    fixId: "integration",
  },
  {
    id: "mcp_server_card",
    title: "Found MCP server",
    category: "Protocol discovery",
    specUrl: "https://modelcontextprotocol.io/",
    maturity: "emerging",
    detection: "http",
    weight: 1,
    impact: "A server card tells an agent which tools the site exposes.",
    fixId: "mcp",
  },
  {
    id: "webmcp",
    title: "WebMCP",
    category: "Protocol discovery",
    specUrl: "https://github.com/webmachinelearning/webmcp",
    maturity: "optional",
    detection: "browser",
    weight: 0,
    impact: "Browser tool registration needs a page runner. This scan records it as not tested.",
  },
  {
    id: "agent_skills",
    title: "Agent Skills",
    category: "Protocol discovery",
    specUrl: "https://agentskills.io/",
    maturity: "optional",
    detection: "http",
    weight: 0,
    impact: "A skills index is optional. Missing it does not change the score.",
    fixId: "mcp",
  },
  {
    id: "oauth_discovery",
    title: "OAuth discovery",
    category: "Auth and trust",
    specUrl: "https://www.rfc-editor.org/rfc/rfc8414",
    maturity: "emerging",
    detection: "http",
    weight: 1,
    impact: "OAuth metadata tells an agent where to authorize.",
    fixId: "integration",
  },
  {
    id: "oauth_protected_resource",
    title: "OAuth protected resource",
    category: "Auth and trust",
    specUrl: "https://www.rfc-editor.org/rfc/rfc9728",
    maturity: "emerging",
    detection: "http",
    weight: 1,
    impact: "A protected-resource document describes which APIs require a token.",
    fixId: "integration",
  },
  {
    id: "a2a_agent_card",
    title: "A2A agent card",
    category: "Auth and trust",
    specUrl: "https://google.github.io/A2A/",
    maturity: "emerging",
    detection: "http",
    weight: 1,
    impact: "An agent card publishes identity and capabilities for agent-to-agent calls.",
    fixId: "mcp",
  },
  {
    id: "ard",
    title: "ARD",
    category: "Auth and trust",
    specUrl: "https://blog.cloudflare.com/agent-readiness",
    maturity: "optional",
    detection: "http",
    weight: 0,
    impact: "An agent resource descriptor is optional. Missing it does not change the score.",
    fixId: "integration",
  },
  {
    id: "auth_md",
    title: "auth.md",
    category: "Auth and trust",
    specUrl: "https://blog.cloudflare.com/agent-readiness",
    maturity: "optional",
    detection: "http",
    weight: 0,
    impact: "auth.md is an optional human-readable auth note. Missing it does not change the score.",
    fixId: "integration",
  },
  {
    id: "x402",
    title: "x402",
    category: "Commerce",
    specUrl: "https://www.x402.org/",
    maturity: "optional",
    detection: "http",
    weight: 0,
    impact: "HTTP 402 payment is recorded. It does not change the score.",
    fixId: "integration",
  },
  {
    id: "mpp",
    title: "MPP",
    category: "Commerce",
    specUrl: "https://blog.cloudflare.com/agent-readiness",
    maturity: "optional",
    detection: "http",
    weight: 0,
    impact: "Machine payment protocol is recorded. It does not change the score.",
    fixId: "integration",
  },
  {
    id: "ucp",
    title: "UCP",
    category: "Commerce",
    specUrl: "https://blog.cloudflare.com/agent-readiness",
    maturity: "optional",
    detection: "http",
    weight: 0,
    impact: "Universal commerce protocol is recorded. It does not change the score.",
    fixId: "integration",
  },
  {
    id: "acp",
    title: "ACP",
    category: "Commerce",
    specUrl: "https://blog.cloudflare.com/agent-readiness",
    maturity: "optional",
    detection: "http",
    weight: 0,
    impact: "Agent commerce protocol is recorded. It does not change the score.",
    fixId: "integration",
  },
  {
    id: "jsonld_organization",
    title: "Identified self",
    category: "Business knowledge",
    specUrl: "https://schema.org/Organization",
    maturity: "established",
    detection: "html",
    weight: 1,
    impact: "Organization markup names the business for agents and search.",
    fixId: "structured_data",
  },
  {
    id: "jsonld_services",
    title: "Services or products",
    category: "Business knowledge",
    specUrl: "https://schema.org/Service",
    maturity: "established",
    detection: "html",
    weight: 1,
    impact: "Service or product markup states what the business offers.",
    fixId: "structured_data",
  },
  {
    id: "jsonld_location_hours",
    title: "Locations and hours",
    category: "Business knowledge",
    specUrl: "https://schema.org/OpeningHoursSpecification",
    maturity: "established",
    detection: "html",
    weight: 1,
    impact: "Address and hours tell an agent where and when the business operates.",
    fixId: "structured_data",
  },
  {
    id: "jsonld_contact",
    title: "Contact",
    category: "Business knowledge",
    specUrl: "https://schema.org/ContactPoint",
    maturity: "established",
    detection: "html",
    weight: 1,
    impact: "A contact point gives an agent a real way to reach the business.",
    fixId: "structured_data",
  },
  {
    id: "jsonld_faq_policy",
    title: "FAQ or policy",
    category: "Business knowledge",
    specUrl: "https://schema.org/FAQPage",
    maturity: "established",
    detection: "html",
    weight: 1,
    impact: "FAQs and policies answer common questions without a person in the loop.",
    fixId: "structured_data",
  },
];

export function agentCheckById(id: string): CheckDefinition | undefined {
  return AGENT_READINESS_CHECKS.find((check) => check.id === id);
}
